import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';
import type { ProjectMemberRow } from '../components/ProjectMembersPanel';

export interface OwnerDirectoryEntry {
  /** The exact string stored in carried_forward_owner / escalation_owner. */
  key: string;
  label: string;
}

interface StakeholderRow {
  id: string;
  name: string;
  email: string;
  status: 'PENDING' | 'ACTIVE' | 'REVOKED';
}

export interface OwnerDirectory {
  entries: OwnerDirectoryEntry[];
  /** True once both source fetches have settled (success or failure). */
  loaded: boolean;
}

/**
 * #3165 — the named-party directory behind the Risk carry-forward and Open
 * Question escalation "Owner" fields.
 *
 * This is deliberately NOT useProjectOwnership (#2846)'s candidate list
 * re-used wholesale: that hook backs the `ownerId` FK re-assignment control,
 * a closed set of platform accounts. carried_forward_owner / escalation_owner
 * are plain free-text columns that, per #2544, intentionally also accept a
 * named party that is **not** a platform user account — a team, a role, or
 * an external party ("Architecture Team", "client sponsor", "platform
 * team" are real values in use today). So this directory is an ADDITIVE
 * suggestion list, not a closed enum: every caller pairs it with a plain-text
 * "Other" escape hatch (see DispositionCard's directory field variant) —
 * never render it as the only way to set these two fields.
 *
 * Candidates: every project_members row (the internal team — same
 * GET /projects/:id/members endpoint useProjectOwnership already calls) plus
 * every ACTIVE project_stakeholders row (named client-side people).
 * PENDING/REVOKED stakeholders are excluded — an invite that hasn't been
 * accepted, or an access grant that's been revoked, is not someone work
 * should currently be routed to.
 *
 * Keyed by display name, not userId/stakeholder id: the columns this feeds
 * are plain strings with no FK, so there is nothing to reconcile a stored id
 * against later — the name IS the stored value.
 */
export function useOwnerDirectory(projectId: string | undefined): OwnerDirectory {
  const [members, setMembers] = useState<ProjectMemberRow[]>([]);
  const [stakeholders, setStakeholders] = useState<StakeholderRow[]>([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [stakeholdersLoaded, setStakeholdersLoaded] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    apiFetch<ProjectMemberRow[]>(`/projects/${projectId}/members`)
      .then((rows) => { if (!cancelled) setMembers(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setMembers([]); })
      .finally(() => { if (!cancelled) setMembersLoaded(true); });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    apiFetch<{ stakeholders: StakeholderRow[] }>(`/projects/${projectId}/stakeholders`)
      .then((data) => { if (!cancelled) setStakeholders(Array.isArray(data?.stakeholders) ? data.stakeholders : []); })
      .catch(() => { if (!cancelled) setStakeholders([]); })
      .finally(() => { if (!cancelled) setStakeholdersLoaded(true); });
    return () => { cancelled = true; };
  }, [projectId]);

  return useMemo(() => {
    const byKey = new Map<string, OwnerDirectoryEntry>();

    for (const m of members) {
      if (!m.userName) continue;
      if (!byKey.has(m.userName)) byKey.set(m.userName, { key: m.userName, label: m.userName });
    }

    for (const s of stakeholders) {
      if (s.status !== 'ACTIVE' || !s.name) continue;
      // A member and a stakeholder sharing a display name collapse to one
      // entry (first-write-wins, members checked first above) rather than
      // offering two rows a caller can't tell apart.
      if (!byKey.has(s.name)) byKey.set(s.name, { key: s.name, label: `${s.name} (client)` });
    }

    const entries = [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
    return { entries, loaded: membersLoaded && stakeholdersLoaded };
  }, [members, stakeholders, membersLoaded, stakeholdersLoaded]);
}
